import { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Buildings, Baby, GraduationCap, CurrencyCircleDollar, TrendUp, Path, ChartLineUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, RadialBarChart, RadialBar, Legend } from "recharts";

const SOP_CHECKLIST = [
  { area: "Brand standards", score: 92 },
  { area: "Hygiene checklist", score: 96 },
  { area: "Safety drills", score: 84 },
  { area: "Curriculum delivery", score: 88 },
  { area: "Parent communication", score: 94 },
];

export default function HqDashboard() {
  const [overview, setOverview] = useState(null);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      const [o, b] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/analytics/branch-performance"),
      ]);
      setOverview(o.data); setBranches(b.data);
    })();
  }, []);

  const totalRevenue = branches.reduce((s, b) => s + (b.fees_collected || 0), 0);
  const totalDue = branches.reduce((s, b) => s + (b.fees_due || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Franchise HQ"
        title="Your brand, every centre, one heartbeat."
        subtitle="Multi-country control with local warmth."
      />

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Centres" value={branches.length} icon={Buildings} accent="terracotta" />
          <StatCard label="Active students" value={overview.students_active} icon={Baby} accent="sage" />
          <StatCard label="Group revenue" value={formatMoney(totalRevenue)} icon={CurrencyCircleDollar} accent="mustard" hint={`${formatMoney(totalDue)} pending`} />
          <StatCard label="Avg. conversion" value={`${overview.conversion_rate}%`} icon={TrendUp} accent="lavender" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Centre performance" className="lg:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={branches.map(b => ({ name: b.city, students: b.students, conv: b.conversion_rate, fees: b.fees_collected / 1000 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="students" name="Students" fill="#E56B55" radius={[6,6,0,0]} />
                <Bar dataKey="conv" name="Conversion %" fill="#F4B731" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="SOP compliance">
          <div className="space-y-3">
            {SOP_CHECKLIST.map(s => (
              <div key={s.area}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{s.area}</span>
                  <span className="text-ink-soft">{s.score}%</span>
                </div>
                <div className="h-2 bg-paper-alt rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-sage-deep rounded-full" style={{ width: `${s.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Centres" action={<Link to="/app/branches" className="text-sm text-terracotta font-semibold">All centres →</Link>}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map(b => (
            <div key={b.branch_id} className="p-5 rounded-3xl border border-border bg-paper-alt">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white border border-border flex items-center justify-center text-2xl">🏫</div>
                <div>
                  <div className="font-heading font-bold">{b.branch_name}</div>
                  <div className="text-xs text-ink-soft">{b.city} · {b.country}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="font-heading font-bold text-xl">{b.students}</div>
                  <div className="text-xs text-ink-soft">Students</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl">{b.conversion_rate}%</div>
                  <div className="text-xs text-ink-soft">Conv.</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl">{formatMoney(b.fees_collected, b.currency).replace(/^.{1}/, '')}k</div>
                  <div className="text-xs text-ink-soft">Revenue</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
